import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import {
  ComposedChart,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  LabelList,
} from "recharts";
import {
  Card,
  Row,
  Col,
  Button,
  Select,
  DatePicker,
  Table,
  Tag,
  Space,
  Typography,
  Statistic,
} from "antd";
import {
  FilterOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  PrinterOutlined,
  ReloadOutlined,
  EyeOutlined,
  InboxOutlined,
  WalletOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import { getHeaders } from "../../utility/getHeader";
import fetchData from "../../functions/fetchData";
import { fetchComboData } from "../../services/api";
import logo from "../../assets/logo.png";
import { exportPurchaseSummaryPdf } from "../reports/billReportPdf";

const { Text } = Typography;
const { Option } = Select;

const numericFields = ['sno', 'id', 'opening_stock', 'stock_in', 'stock_out', 'closing_stock', 'purchase_price', 'vat', 'subtotal', 'vatAmount', 'netAmount', 'total_amount'];
const moneyFields = ['purchase_price', 'subtotal', 'vatAmount', 'netAmount', 'total_amount'];
const percentFields = ['vat'];
const dateFields = ['purchase_date'];

const formatPurchaseDate = (value) => (value ? dayjs(value).format('DD-MM-YYYY') : '-');
const purchaseAmount = (r) => parseFloat(r.netAmount) || parseFloat(r.subtotal) || 0;
const money = (v) => (parseFloat(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function StockReport() {
  const [filteredData, setFilteredData] = useState([]);
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filters, setFilters] = useState({
    item_id: "",
    supplier_id: "",
    fromDate: "",
    toDate: "",
  });
  const [viewingClosingStock, setViewingClosingStock] = useState(false);
  const [purchaseView, setPurchaseView] = useState(false); // purchase details (stock-in records only)
  const [pdfOrder, setPdfOrder] = useState("asc");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const fieldSets = {
    closing: [
      ["S.No.", "sno", "center"],
      ["Item Name", "item_name"],
      ["Unit", "unit"],
      ["Last Purchase Date", "purchase_date"],
      ["Purchase Price", "purchase_price", "right"],
      ["Closing Stock", "closing_stock", "center"],
      ["Total Amount", "total_amount", "right"],
    ],
    bySupplier: [
      ["ID", "id"],
      ["Item Name", "item_name"],
      ["Supplier Name", "supplier_name"],
      ["Date of Purchase", "purchase_date"],
      ["Opening Stock", "opening_stock", "right"],
      ["Stock In", "stock_in", "right"],
      ["Stock Out", "stock_out", "right"],
      ["Closing Stock", "closing_stock", "center"],
      ["Unit", "unit"],
      ["Purchase Price", "purchase_price", "right"],
      ["VAT %", "vat", "right"],
      ["Subtotal", "subtotal", "right"],
      ["Net Amount", "netAmount", "right"],
    ],
    purchase: [
      ["Date", "purchase_date", "center"],
      ["Ref No", "refno", "center"],
      ["Item Name", "item_name"],
      ["Supplier Name", "supplier_name"],
      ["Qty", "stock_in", "right"],
      ["Unit", "unit"],
      ["Rate", "purchase_price", "right"],
      ["Subtotal", "subtotal", "right"],
      ["VAT %", "vat", "right"],
      ["VAT Amount", "vatAmount", "right"],
      ["Net Amount", "netAmount", "right"],
    ],
    general: [
      ["ID", "id"],
      ["Item Name", "item_name"],
      ["Supplier Name", "supplier_name"],
      ["Date of Purchase", "purchase_date"],
      ["Opening Stock", "opening_stock", "right"],
      ["Stock In", "stock_in", "right"],
      ["Stock Out", "stock_out", "right"],
      ["Closing Stock", "closing_stock", "center"],
      ["Unit", "unit"],
    ],
  };

  const makeSorter = (field) => (a, b) => {
    if (numericFields.includes(field)) {
      return (parseFloat(a[field]) || 0) - (parseFloat(b[field]) || 0);
    }
    if (dateFields.includes(field)) {
      return new Date(a[field] || 0) - new Date(b[field] || 0);
    }
    return String(a[field] || '').localeCompare(String(b[field] || ''));
  };

  const renderCell = (field) => (value) => {
    let content = value;
    if (content === undefined || content === null || content === '' || (typeof content === 'number' && isNaN(content))) {
      content = '-';
    }
    if (moneyFields.includes(field)) {
      const num = parseFloat(content);
      content = `฿${(isNaN(num) ? 0 : num).toFixed(2)}`;
    }
    if (percentFields.includes(field)) {
      const num = parseFloat(content);
      content = !isNaN(num) && num > 0 ? `${num}%` : '-';
    }
    if (dateFields.includes(field)) {
      return formatPurchaseDate(value);
    }
    if (field === 'closing_stock') {
      const stockValue = parseFloat(value);
      const color = stockValue < 10 ? 'red' : stockValue < 50 ? 'orange' : 'green';
      return <Tag color={color}>{content}</Tag>;
    }
    if (field === 'item_name') {
      return <Text strong style={{ color: '#0d9488' }}>{content}</Text>;
    }
    if (field === 'netAmount' || field === 'total_amount') {
      return <Text strong style={{ color: '#059669' }}>{content}</Text>;
    }
    return content;
  };

  const showPurchase = purchaseView && !viewingClosingStock;
  const fieldSet = viewingClosingStock ? fieldSets.closing : showPurchase ? fieldSets.purchase : filters.supplier_id ? fieldSets.bySupplier : fieldSets.general;
  const columns = fieldSet.map(([label, field, align]) => ({
    title: label,
    dataIndex: field,
    key: field,
    align,
    sorter: makeSorter(field),
    render: renderCell(field),
  }));

  // In purchase view only stock-in records are shown; everything else shows the raw rows.
  const displayData = useMemo(
    () => (showPurchase ? filteredData.filter((r) => parseFloat(r.stock_in) > 0) : filteredData),
    [filteredData, showPurchase]
  );

  const orderRows = (rows, dir) => {
    const d = dir === "desc" ? -1 : 1;
    const time = (r) => new Date(r.purchase_date || 0).getTime() || 0;
    return [...rows].sort((a, b) => d * (time(a) - time(b) || (parseInt(a.id) || 0) - (parseInt(b.id) || 0)));
  };

  const purchaseStats = useMemo(() => {
    const monthly = {};
    const sup = {};
    let qty = 0, sub = 0, vat = 0, net = 0;
    displayData.forEach((r) => {
      const v = purchaseAmount(r);
      qty += parseFloat(r.stock_in) || 0;
      sub += parseFloat(r.subtotal) || 0;
      vat += parseFloat(r.vatAmount) || 0;
      net += v;
      const d = dayjs(r.purchase_date);
      if (r.purchase_date && d.isValid()) {
        const k = d.format('YYYY-MM');
        monthly[k] = monthly[k] || { month: d.format('MMM YYYY'), total: 0, records: 0 };
        monthly[k].total += v;
        monthly[k].records += 1;
      }
      const s = r.supplier_name || 'Unknown';
      sup[s] = (sup[s] || 0) + v;
    });
    return {
      qty, sub, vat, net,
      monthly: Object.keys(monthly).sort().map((k) => ({ ...monthly[k], total: Math.round(monthly[k].total * 100) / 100 })),
      suppliers: Object.entries(sup)
        .map(([name, total]) => ({ name, total: Math.round(total * 100) / 100 }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 8),
    };
  }, [displayData]);

  const selectedRows = displayData.filter((r, i) => selectedRowKeys.includes(r.id || i));

  const periodLabel = () => {
    const dates = displayData.map((r) => dayjs(r.purchase_date)).filter((d) => d.isValid()).sort((a, b) => a - b);
    const from = filters.fromDate || (dates.length ? dates[0].format('YYYY-MM-DD') : '');
    const to = filters.toDate || (dates.length ? dates[dates.length - 1].format('YYYY-MM-DD') : '');
    return from || to ? `${from || '...'}  to  ${to || '...'}` : 'All dates';
  };

  const downloadPurchasePdf = (rows) => {
    if (!rows.length) {
      toast.error("No purchase records to export");
      return;
    }
    const sup = suppliers.find((s) => String(s.id) === String(filters.supplier_id));
    exportPurchaseSummaryPdf({
      rows: orderRows(rows, pdfOrder),
      logo,
      periodLabel: periodLabel(),
      supplierLabel: sup ? (sup.supplier_name || sup.company_name) : "All suppliers",
      fileName: `purchase_report_${dayjs().format('YYYY-MM-DD')}.pdf`,
    });
  };

  const printSelected = () => {
    if (!selectedRows.length) return toast.info("Select at least one record");
    const rows = orderRows(selectedRows, pdfOrder);
    const head = fieldSets.purchase.map(([label]) => `<th>${label}</th>`).join('');
    const cell = (field, r) => {
      const v = r[field];
      if (dateFields.includes(field)) return formatPurchaseDate(v);
      if (moneyFields.includes(field)) return money(v);
      return v ?? '-';
    };
    const body = rows
      .map((r) => `<tr>${fieldSets.purchase.map(([, f, a]) => `<td style="text-align:${a || 'left'}">${cell(f, r)}</td>`).join('')}</tr>`)
      .join('');
    const w = window.open('', '_blank', 'width=1000,height=700');
    if (!w) return toast.error('Popup blocked. Please allow popups to print.');
    w.document.write(`<html><head><title>Purchase Report</title><style>
      body{font-family:Arial,sans-serif;margin:20px}h2{text-align:center}
      table{width:100%;border-collapse:collapse;font-size:12px}th,td{border:1px solid #333;padding:6px}
      th{background:#0d9488;color:#fff}tfoot td{font-weight:bold;background:#f0fdfa}
    </style></head><body><h2>Purchase Report (${rows.length} records)</h2>
    <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody>
    <tfoot><tr><td colspan="10" style="text-align:right">Total Net Amount</td><td style="text-align:right">${money(rows.reduce((a, r) => a + purchaseAmount(r), 0))}</td></tr></tfoot>
    </table></body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  const fetchInventory = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.supplier_id) params.append('supplier_id', filters.supplier_id);
      if (filters.item_id) params.append('item_id', filters.item_id);
      if (filters.fromDate) params.append('start_date', filters.fromDate);
      if (filters.toDate) params.append('end_date', filters.toDate);

      const res = await axios.get(`/inventory/stock-report?${params.toString()}`, getHeaders());
      const inventoryData = res.data?.data?.inventory || [];
      setFilteredData(inventoryData);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch stock report data");
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      await fetchInventory();
      fetchComboData("items", "id").then((res) => {
        setItems(res.filter(item => item.isstockable === '1' || item.isstockable === 1));
      });
      fetchData("suppliers", setSuppliers, "id", {});
    };

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const autoApplyFilters = async (newFilters) => {
    try {
      const params = new URLSearchParams();
      if (newFilters.supplier_id) params.append('supplier_id', newFilters.supplier_id);
      if (newFilters.item_id) params.append('item_id', newFilters.item_id);
      if (newFilters.fromDate) params.append('start_date', newFilters.fromDate);
      if (newFilters.toDate) params.append('end_date', newFilters.toDate);

      const res = await axios.get(`/inventory/stock-report?${params.toString()}`, getHeaders());
      const inventoryData = res.data?.data?.inventory || [];
      setFilteredData(inventoryData);
      setViewingClosingStock(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to apply filters");
    }
  };

  const applyFilters = () => autoApplyFilters(filters);

  const resetFilters = async () => {
    const reset = { item_id: "", supplier_id: "", fromDate: "", toDate: "" };
    setFilters(reset);
    setViewingClosingStock(false);
    setPurchaseView(false);
    setSelectedRowKeys([]);

    try {
      const res = await axios.get('/inventory/stock-report', getHeaders());
      const inventoryData = res.data?.data?.inventory || [];
      setFilteredData(inventoryData);
    } catch (err) {
      console.error(err);
      toast.error("Failed to reset filters");
    }
  };

  const viewClosingStock = async () => {
    try {
      const res = await axios.get('/inventory/current-stock-summary', getHeaders());
      const stockItems = res.data?.data?.items || [];

      let final = stockItems.map((item, index) => ({
        sno: index + 1,
        item_name: item.item_name,
        unit: item.unit || 'PCS',
        purchase_date: item.last_purchase_date || null,
        purchase_price: parseFloat(item.last_purchase_price || 0),
        closing_stock: parseFloat(item.current_stock || 0),
        total_amount: parseFloat(item.stock_value || 0),
      }));

      final = final.sort((a, b) => a.item_name.localeCompare(b.item_name));
      final = final.map((item, index) => ({ ...item, sno: index + 1 }));

      setFilteredData(final);
      setViewingClosingStock(true);
      setPurchaseView(false);
      setSelectedRowKeys([]);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch closing stock summary");
    }
  };

  const handlePrint = () => {
    const tableContainer = document.getElementById("tableid");
    if (!tableContainer) {
      toast.error("No data available to print.");
      return;
    }

    const table = tableContainer.querySelector("table");
    if (!table) {
      toast.error("No table found to print.");
      return;
    }

    const printWindow = window.open("", "_blank", "width=900,height=700");
    printWindow.document.write(`
    <html>
      <head>
        <title>Stock Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { text-align: center; margin-bottom: 0; }
          h2 { text-align: center; margin-top: 5px; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; }
          table, th, td { border: 1px solid black; }
          th, td { padding: 8px; text-align: left; }
        </style>
      </head>
      <body>
        <h2>Stock Report</h2>
        ${table.outerHTML}
      </body>
    </html>
  `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const exportToPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

    const logoImg = new Image();
    logoImg.src = "/logo.png";

    const buildTable = () => {
      doc.setFontSize(16);
      doc.text("Stock Report", 14, 20);

      const tableColumn = columns.map((col) => col.title);
      const tableRows = displayData.map((row) =>
        columns.map((col) => dateFields.includes(col.dataIndex) ? formatPurchaseDate(row[col.dataIndex]) : (row[col.dataIndex] ?? ""))
      );

      doc.autoTable({
        startY: 30,
        head: [tableColumn],
        body: tableRows,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [13, 148, 136] },
        columnStyles: {
          [tableColumn.length - 1]: { cellWidth: 40, halign: "center" },
        },
        foot: [
          tableColumn.map((col) => {
            if (col === "Total Amount" || col === "Net Amount") {
              const field = col === "Total Amount" ? "total_amount" : "netAmount";
              const total = displayData.reduce((acc, item) => acc + parseFloat(item[field] || 0), 0);
              return `Total: ฿${total.toFixed(2)}`;
            }
            return "";
          }),
        ],
        footStyles: { fontStyle: "bold", halign: "center" },
      });

      doc.save("stock_report.pdf");
    };

    logoImg.onload = () => {
      doc.addImage(logoImg, "PNG", 250, 10, 35, 15);
      buildTable();
    };

    logoImg.onerror = () => {
      buildTable();
    };
  };

  const exportToExcel = () => {
    if (displayData.length === 0) {
      toast.error("No records to export");
      return;
    }
    const worksheetData = displayData.map((row) => {
      const obj = {};
      columns.forEach((col) => {
        obj[col.title] = dateFields.includes(col.dataIndex) ? formatPurchaseDate(row[col.dataIndex]) : (row[col.dataIndex] ?? '');
      });
      return obj;
    });
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Report");
    XLSX.writeFile(workbook, `stock_report_${dayjs().format('YYYY-MM-DD')}.xlsx`);
  };

  const totalAmountField = viewingClosingStock ? 'total_amount' : (filters.supplier_id || showPurchase) ? 'netAmount' : null;
  const grandTotal = totalAmountField
    ? displayData.reduce((acc, item) => acc + parseFloat(item[totalAmountField] || 0), 0)
    : 0;

  return (
    <Layout>
      <Header title="Stock Report" />
      <ToastContainer />

      <div style={{ padding: 20 }}>
        {/* Filter Card */}
        <Card
          title={
            <Space>
              <FilterOutlined style={{ color: '#0d9488' }} />
              <Text strong>Filter Stock Report</Text>
            </Space>
          }
          style={{ background: '#f0fdfa', borderColor: '#99f6e4' }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={6}>
              <Text type="secondary" style={{ fontSize: 12 }}>Item Name</Text>
              <Select
                allowClear
                showSearch
                style={{ width: '100%' }}
                placeholder="All Items"
                value={filters.item_id || undefined}
                onChange={(value) => setFilters((prev) => {
                  const next = { ...prev, item_id: value || "" };
                  autoApplyFilters(next);
                  return next;
                })}
                filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
              >
                {items.map((item) => (
                  <Option key={item.id} value={item.id}>{item.iname}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} md={6}>
              <Text type="secondary" style={{ fontSize: 12 }}>Supplier Name</Text>
              <Select
                allowClear
                showSearch
                style={{ width: '100%' }}
                placeholder="All Suppliers"
                value={filters.supplier_id || undefined}
                onChange={(value) => setFilters((prev) => {
                  const next = { ...prev, supplier_id: value || "" };
                  autoApplyFilters(next);
                  return next;
                })}
                filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
              >
                {suppliers.map((supplier) => (
                  <Option key={supplier.id} value={supplier.id}>
                    {supplier.supplier_name || supplier.company_name || 'Unknown Supplier'}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} md={5}>
              <Text type="secondary" style={{ fontSize: 12 }}>From Date</Text>
              <DatePicker
                style={{ width: '100%' }}
                format="DD-MM-YYYY"
                value={filters.fromDate ? dayjs(filters.fromDate) : null}
                onChange={(date, dateString) => setFilters((prev) => {
                  const next = { ...prev, fromDate: date ? date.format('YYYY-MM-DD') : "" };
                  autoApplyFilters(next);
                  return next;
                })}
              />
            </Col>
            <Col xs={24} md={5}>
              <Text type="secondary" style={{ fontSize: 12 }}>To Date</Text>
              <DatePicker
                style={{ width: '100%' }}
                format="DD-MM-YYYY"
                value={filters.toDate ? dayjs(filters.toDate) : null}
                onChange={(date) => setFilters((prev) => {
                  const next = { ...prev, toDate: date ? date.format('YYYY-MM-DD') : "" };
                  autoApplyFilters(next);
                  return next;
                })}
              />
            </Col>
            <Col xs={24} md={2} style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button type="primary" block onClick={applyFilters}>Apply</Button>
            </Col>
          </Row>

          <Row style={{ marginTop: 16 }}>
            <Col span={24}>
              <Space wrap>
                <Button icon={<EyeOutlined />} onClick={viewClosingStock}>View Closing Stock</Button>
                <Button
                  type={showPurchase ? "primary" : "default"}
                  icon={<ShoppingCartOutlined />}
                  onClick={() => {
                    setSelectedRowKeys([]);
                    if (showPurchase) {
                      setPurchaseView(false);
                    } else {
                      setPurchaseView(true);
                      autoApplyFilters(filters);
                    }
                  }}
                >
                  {showPurchase ? "Back to Stock Ledger" : "Purchase Details"}
                </Button>
                <Button icon={<ReloadOutlined />} onClick={resetFilters}>Reset</Button>
                <Button icon={<PrinterOutlined />} onClick={handlePrint}>Print</Button>
                <Button icon={<FilePdfOutlined />} danger onClick={exportToPDF}>Export PDF</Button>
                <Button icon={<FileExcelOutlined />} onClick={exportToExcel} style={{ color: "#389e0d", borderColor: "#389e0d" }}>Export Excel</Button>
                {showPurchase && (
                  <>
                    <Select
                      style={{ width: 190 }}
                      value={pdfOrder}
                      onChange={setPdfOrder}
                      options={[
                        { value: "asc", label: "PDF: Oldest first (ASC)" },
                        { value: "desc", label: "PDF: Newest first (DESC)" },
                      ]}
                    />
                    <Button type="primary" danger icon={<FilePdfOutlined />} onClick={() => downloadPurchasePdf(displayData)}>
                      Purchase Summary PDF
                    </Button>
                  </>
                )}
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Summary Cards */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={12} md={8}>
            <Card style={{ background: '#f0fdfa' }}>
              <Statistic
                title={viewingClosingStock ? 'Closing Stock Items' : showPurchase ? 'Purchase Records' : 'Total Records'}
                value={displayData.length}
                prefix={<InboxOutlined />}
                valueStyle={{ color: '#0d9488' }}
              />
            </Card>
          </Col>
          {totalAmountField && (
            <Col xs={12} md={8}>
              <Card style={{ background: '#ecfdf5' }}>
                <Statistic
                  title={viewingClosingStock ? 'Total Stock Value' : 'Grand Total (Net Amount)'}
                  value={grandTotal}
                  precision={2}
                  prefix={<><WalletOutlined /> ฿</>}
                  valueStyle={{ color: '#059669' }}
                />
              </Card>
            </Col>
          )}
        </Row>

        {showPurchase && (
          <>
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              {[
                ['Total Qty', purchaseStats.qty.toLocaleString('en-US', { maximumFractionDigits: 2 }), '#2563eb', ''],
                ['Subtotal', money(purchaseStats.sub), '#d97706', '฿ '],
                ['VAT', money(purchaseStats.vat), '#ea580c', '฿ '],
                ['Avg / Purchase', money(displayData.length ? purchaseStats.net / displayData.length : 0), '#7c3aed', '฿ '],
              ].map(([title, value, color, prefix]) => (
                <Col xs={12} md={6} key={title}>
                  <Card><Statistic title={title} value={value} prefix={prefix} valueStyle={{ color }} /></Card>
                </Col>
              ))}
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col xs={24} lg={14}>
                <Card title={<Text strong>Monthly Purchase Summary</Text>}>
                  {purchaseStats.monthly.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40, color: '#8c8c8c' }}>No purchase data</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={320}>
                      <ComposedChart data={purchaseStats.monthly} margin={{ top: 24, right: 16, left: 8, bottom: 4 }}>
                        <defs>
                          <linearGradient id="purGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#2dd4bf" />
                            <stop offset="100%" stopColor="#0d9488" stopOpacity={0.6} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis dataKey="month" tickLine={false} />
                        <YAxis yAxisId="amt" tickLine={false} axisLine={false}
                          tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(v % 1000 ? 1 : 0)}k` : v)} />
                        <YAxis yAxisId="cnt" orientation="right" tickLine={false} axisLine={false} allowDecimals={false} />
                        <ChartTooltip formatter={(v, n) => (n === 'Net Amount' ? [`฿ ${money(v)}`, n] : [v, n])} />
                        <Legend />
                        <Bar yAxisId="amt" dataKey="total" name="Net Amount" fill="url(#purGradient)" radius={[6, 6, 0, 0]} maxBarSize={56}>
                          <LabelList dataKey="total" position="top" style={{ fontSize: 11, fill: '#595959' }}
                            formatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v)} />
                        </Bar>
                        <Line yAxisId="cnt" type="monotone" dataKey="records" name="Records" stroke="#fa8c16" strokeWidth={2} dot={{ r: 3 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </Card>
              </Col>
              <Col xs={24} lg={10}>
                <Card title={<Text strong>Top Suppliers</Text>}>
                  {purchaseStats.suppliers.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40, color: '#8c8c8c' }}>No purchase data</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={320}>
                      <BarChart data={purchaseStats.suppliers} layout="vertical" margin={{ top: 8, right: 40, left: 8, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                        <XAxis type="number" tickLine={false} axisLine={false}
                          tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
                        <YAxis type="category" dataKey="name" width={100} tickLine={false} axisLine={false} />
                        <ChartTooltip formatter={(v) => [`฿ ${money(v)}`, 'Net Amount']} />
                        <Bar dataKey="total" fill="#0d9488" radius={[0, 6, 6, 0]} maxBarSize={26}>
                          <LabelList dataKey="total" position="right" style={{ fontSize: 11, fill: '#595959' }}
                            formatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v)} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Card>
              </Col>
            </Row>
          </>
        )}

        {/* Data Table */}
        <Card
          style={{ marginTop: 16 }}
          title={
            <Space>
              <InboxOutlined style={{ color: '#0d9488' }} />
              <Text strong>{viewingClosingStock ? 'Closing Stock Report' : showPurchase ? 'Purchase Report Details' : 'Inventory Stock Report'}</Text>
            </Space>
          }
        >
          {showPurchase && (
            <div style={{
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 12, padding: '8px 12px', borderRadius: 8,
              background: selectedRowKeys.length ? '#f0fdfa' : '#fafafa', border: '1px solid ' + (selectedRowKeys.length ? '#5eead4' : '#f0f0f0'),
            }}>
              <Text strong>
                {selectedRowKeys.length ? `${selectedRowKeys.length} record(s) selected` : 'Select records to print or download together'}
              </Text>
              <Button size="small" onClick={() => setSelectedRowKeys(displayData.map((r, i) => r.id || i))} disabled={!displayData.length}>
                Select all {displayData.length}
              </Button>
              <Button size="small" onClick={() => setSelectedRowKeys([])} disabled={!selectedRowKeys.length}>Clear selection</Button>
              <div style={{ flex: 1 }} />
              <Button type="primary" icon={<PrinterOutlined />} onClick={printSelected} disabled={!selectedRowKeys.length}>Print Selected</Button>
              <Button type="primary" danger icon={<FilePdfOutlined />} onClick={() => downloadPurchasePdf(selectedRows)} disabled={!selectedRowKeys.length}>
                Download Selected PDF
              </Button>
            </div>
          )}
          <div id="tableid">
            <Table
              rowSelection={showPurchase ? { selectedRowKeys, onChange: setSelectedRowKeys, preserveSelectedRowKeys: true } : undefined}
              rowKey={(row, index) => row.id || index}
              columns={columns}
              dataSource={displayData}
              pagination={{ pageSize: 15, showSizeChanger: true, showTotal: (t) => `${t} records` }}
              scroll={{ x: true }}
              locale={{ emptyText: 'No stock entries available' }}
            />
          </div>
        </Card>
      </div>
    </Layout>
  );
}
